import React, { useEffect, useMemo, useState } from 'react';
import { collection, deleteDoc, doc, getDocs, getFirestore, query, serverTimestamp, setDoc, updateDoc, where } from 'firebase/firestore';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import defaultProfileImage from '../assets/default-profile.png';
import './ProfilesPage.css';

const Icon = ({ name }) => {
  const paths = {
    search: <><circle cx="11" cy="11" r="6"/><path d="m16 16 4 4"/></>,
    users: <><circle cx="9" cy="8" r="3"/><circle cx="17" cy="9" r="2"/><path d="M3 19c0-4 2-6 6-6s6 2 6 6M15 14c3 0 5 2 5 5"/></>,
    connections: <><circle cx="8" cy="8" r="3"/><circle cx="17" cy="8" r="3"/><path d="M2 20c0-4 2-7 6-7s6 3 6 7M13 14c1-.7 2.2-1 4-1 3.5 0 5 3 5 7"/></>,
    request: <><circle cx="9" cy="8" r="3"/><path d="M3 19c0-4 2-6 6-6 2 0 3.5.5 4.5 1.5M18 12v6M15 15h6"/></>,
    calendar: <><path d="M5 4h14v16H5Z"/><path d="M8 2v4M16 2v4M5 9h14"/></>,
    pin: <><path d="M20 10c0 5-8 11-8 11S4 15 4 10a8 8 0 1 1 16 0Z"/><circle cx="12" cy="10" r="2"/></>,
    shield: <><path d="M12 3 4 6v5c0 5 3.5 8.5 8 10 4.5-1.5 8-5 8-10V6Z"/><path d="m9 12 2 2 4-5"/></>,
    close: <path d="m7 7 10 10M17 7 7 17"/>,
    check: <path d="m5 12 4 4L19 6"/>,
    chevron: <path d="m9 18 6-6-6-6"/>,
  };
  return <svg className="ui-icon" viewBox="0 0 24 24" aria-hidden="true">{paths[name]}</svg>;
};

const profileRole = (profile) => profile.role || profile.certification || 'Climbing partner';

function ProfilesPage() {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const db = getFirestore();
  const [profiles, setProfiles] = useState([]);
  const [requests, setRequests] = useState([]);
  const [dismissed, setDismissed] = useState([]);
  const [busyUid, setBusyUid] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionError, setActionError] = useState('');
  const [search, setSearch] = useState('');
  const goalsQuery = new URLSearchParams(location.search).get('goals') || '';

  useEffect(() => setSearch(goalsQuery), [goalsQuery]);

  useEffect(() => {
    const loadNetwork = async () => {
      try {
        const profilesSnapshot = await getDocs(collection(db, 'users'));
        setProfiles(profilesSnapshot.docs.map((profileDoc) => ({ id: profileDoc.id, ...profileDoc.data() })));

        if (currentUser) {
          try {
            const requestsRef = collection(db, 'connectionRequests');
            const [incomingSnapshot, outgoingSnapshot] = await Promise.all([
              getDocs(query(requestsRef, where('toUid', '==', currentUser.uid))),
              getDocs(query(requestsRef, where('fromUid', '==', currentUser.uid))),
            ]);
            const requestMap = new Map();
            [...incomingSnapshot.docs, ...outgoingSnapshot.docs].forEach((requestDoc) => requestMap.set(requestDoc.id, { id: requestDoc.id, ...requestDoc.data() }));
            setRequests(Array.from(requestMap.values()));
          } catch (requestsError) {
            console.error('Error loading connection requests:', requestsError);
            setActionError('Profiles loaded, but connection invitations are temporarily unavailable.');
          }
        }
      } catch (loadError) {
        console.error('Error loading network:', loadError);
        setError('We could not load the climbing network. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    loadNetwork();
  }, [currentUser, db]);

  const profilesByUid = useMemo(() => new Map(profiles.map((profile) => [profile.uid, profile])), [profiles]);
  const relationshipByUid = useMemo(() => {
    const relationships = new Map();
    requests.forEach((requestItem) => {
      const otherUid = requestItem.fromUid === currentUser?.uid ? requestItem.toUid : requestItem.fromUid;
      if (otherUid) relationships.set(otherUid, requestItem.status === 'accepted' ? 'connected' : requestItem.fromUid === currentUser?.uid ? 'pending' : 'incoming');
    });
    return relationships;
  }, [currentUser, requests]);

  const invitations = useMemo(() => requests.filter((requestItem) => requestItem.toUid === currentUser?.uid && requestItem.status === 'pending').map((requestItem) => ({ request: requestItem, profile: profilesByUid.get(requestItem.fromUid) })).filter((item) => item.profile), [currentUser, profilesByUid, requests]);
  const connectionCount = useMemo(() => new Set(requests.filter((requestItem) => requestItem.status === 'accepted').map((requestItem) => requestItem.fromUid === currentUser?.uid ? requestItem.toUid : requestItem.fromUid)).size, [currentUser, requests]);
  const pendingCount = requests.filter((requestItem) => requestItem.fromUid === currentUser?.uid && requestItem.status === 'pending').length;

  const suggestedProfiles = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return profiles.filter((profile) => {
      if (!profile.uid || profile.uid === currentUser?.uid || dismissed.includes(profile.uid) || relationshipByUid.get(profile.uid) === 'incoming') return false;
      const searchable = [profile.name, profile.location, profile.goals, profile.role, profile.certification].filter(Boolean).join(' ').toLowerCase();
      return !needle || searchable.includes(needle);
    });
  }, [currentUser, dismissed, profiles, relationshipByUid, search]);

  const sendRequest = async (profile) => {
    if (!currentUser) {
      navigate('/signup');
      return;
    }
    if (!profile.uid || relationshipByUid.has(profile.uid)) return;
    const requestId = `${currentUser.uid}_${profile.uid}`;
    setBusyUid(profile.uid);
    setActionError('');
    try {
      const requestData = {
        fromUid: currentUser.uid,
        toUid: profile.uid,
        status: 'pending',
        createdAt: serverTimestamp(),
      };
      await setDoc(doc(db, 'connectionRequests', requestId), requestData);
      setRequests((current) => [...current, { id: requestId, ...requestData }]);
    } catch (requestError) {
      console.error('Could not send connection request:', requestError);
      setActionError('Your request could not be sent. Please try again.');
    } finally {
      setBusyUid('');
    }
  };

  const acceptRequest = async (requestItem) => {
    setBusyUid(requestItem.fromUid);
    setActionError('');
    try {
      await updateDoc(doc(db, 'connectionRequests', requestItem.id), { status: 'accepted', respondedAt: serverTimestamp() });
      setRequests((current) => current.map((item) => item.id === requestItem.id ? { ...item, status: 'accepted' } : item));
    } catch (requestError) {
      console.error('Could not accept connection:', requestError);
      setActionError('This invitation could not be accepted. Please try again.');
    } finally {
      setBusyUid('');
    }
  };

  const ignoreRequest = async (requestItem) => {
    setBusyUid(requestItem.fromUid);
    setActionError('');
    try {
      await deleteDoc(doc(db, 'connectionRequests', requestItem.id));
      setRequests((current) => current.filter((item) => item.id !== requestItem.id));
      setDismissed((current) => [...current, requestItem.fromUid]);
    } catch (requestError) {
      console.error('Could not ignore invitation:', requestError);
      setActionError('This invitation could not be removed. Please try again.');
    } finally {
      setBusyUid('');
    }
  };

  return (
    <div className="my-network-page">
      <header className="my-network-header">
        <div><span className="eyebrow">CLIMBERLINK NETWORK</span><h1>My Network</h1><p>Grow your rope team and stay connected with climbers you trust.</p></div>
        <label className="network-search"><Icon name="search" /><span className="sr-only">Search climbers</span><input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search by name, skill, or location" /></label>
      </header>

      {actionError && <div className="network-action-error">{actionError}</div>}

      <div className="my-network-layout">
        <aside className="manage-network-card">
          <h2>Manage my network</h2>
          <Link to="/profiles"><span><Icon name="connections" /> Connections</span><strong>{connectionCount}</strong></Link>
          <Link to="/profiles"><span><Icon name="request" /> Sent requests</span><strong>{pendingCount}</strong></Link>
          <Link to="/Trips"><span><Icon name="calendar" /> Expeditions</span><Icon name="chevron" /></Link>
          <div className="network-divider" />
          <Link to="/profiles"><span><Icon name="users" /> Climbing partners</span><Icon name="chevron" /></Link>
          <Link to="/profiles"><span><Icon name="shield" /> Guides and mentors</span><Icon name="chevron" /></Link>
        </aside>

        <main className="network-feed">
          {currentUser && (
            <section className="invitations-panel network-panel">
              <div className="network-panel-heading"><h2>Invitations</h2><span>{invitations.length}</span></div>
              {invitations.length === 0 ? (
                <div className="no-invitations"><Icon name="check" /><div><strong>You’re all caught up</strong><span>New connection invitations will appear here.</span></div></div>
              ) : invitations.map(({ request: requestItem, profile }) => (
                <article className="invitation-row" key={requestItem.id}>
                  <Link to={`/UserProfile/${profile.userId}`}><img src={profile.imageUrl || defaultProfileImage} alt={profile.name || 'Climber'} /></Link>
                  <div className="invitation-copy"><Link to={`/UserProfile/${profile.userId}`}>{profile.name || 'Unnamed climber'}</Link><span>{profileRole(profile)} · {profile.location || 'Location not set'}</span><p>{profile.goals || 'Would like to connect on ClimberLink.'}</p></div>
                  <div className="invitation-actions"><button className="ignore-button" disabled={busyUid === profile.uid} onClick={() => ignoreRequest(requestItem)}>Ignore</button><button className="accept-button" disabled={busyUid === profile.uid} onClick={() => acceptRequest(requestItem)}>{busyUid === profile.uid ? 'Working…' : 'Accept'}</button></div>
                </article>
              ))}
            </section>
          )}

          <section className="suggestions-panel network-panel">
            <div className="network-panel-heading"><div><h2>{search ? `Climbers matching “${search}”` : 'People you may want on your rope team'}</h2><p>Suggestions based on the ClimberLink community</p></div><span>{suggestedProfiles.length}</span></div>
            {loading && <div className="network-empty-state">Loading climbers…</div>}
            {error && <div className="network-empty-state error-state">{error}</div>}
            {!loading && !error && suggestedProfiles.length === 0 && <div className="network-empty-state"><Icon name="search" /><strong>No suggestions match this search</strong><span>Try another name, climbing goal, or location.</span></div>}
            <div className="people-grid">
              {suggestedProfiles.map((profile, index) => {
                const relationship = relationshipByUid.get(profile.uid);
                return (
                  <article className="people-card" key={profile.id}>
                    <div className={`people-cover cover-${index % 3}`} />
                    <button className="dismiss-person" onClick={() => setDismissed((current) => [...current, profile.uid])} aria-label={`Dismiss ${profile.name || 'suggestion'}`}><Icon name="close" /></button>
                    <Link className="people-avatar-link" to={`/UserProfile/${profile.userId}`}><img src={profile.imageUrl || defaultProfileImage} alt={profile.name || 'Climber profile'} /></Link>
                    <div className="people-card-content">
                      <Link className="people-name" to={`/UserProfile/${profile.userId}`}>{profile.name || 'Unnamed climber'}{profile.certification && <Icon name="shield" />}</Link>
                      <p className="people-role">{profileRole(profile)}</p>
                      <p className="people-location"><Icon name="pin" /> {profile.location || 'Location not set'}</p>
                      <p className="people-goal">{profile.goals || 'Open to new climbing objectives.'}</p>
                      <div className="mutual-row"><span className="mutual-avatar">CL</span><span>ClimberLink community</span></div>
                      <button
                        className={`connect-button ${relationship || ''}`}
                        disabled={busyUid === profile.uid || Boolean(relationship)}
                        onClick={() => sendRequest(profile)}
                      >
                        {relationship === 'connected' ? <><Icon name="check" /> Connected</> : relationship === 'pending' ? 'Request sent' : busyUid === profile.uid ? 'Sending…' : <><Icon name="request" /> Connect</>}
                      </button>
                    </div>
                  </article>
                );
              })}
            </div>
          </section>
        </main>
      </div>
    </div>
  );
}

export default ProfilesPage;
